import { getPortalBaseUrl } from "../core/config.js";

const STORAGE_PREFIX = "usersound_session:";

function embedHost(): string {
  if (typeof window === "undefined" || !window.location) return "";
  return window.location.host ?? "";
}

function sessionStorageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${embedHost()}:${projectId.trim()}`;
}

/** Minimal context shape for session resolution (avoids importing portal.ts). */
export type SessionPortalContext = {
  user_id?: string;
  response_id?: string;
  [key: string]: string | undefined;
};

/**
 * Normalizes external user id the same way as the legacy embed: empty and
 * common null-like strings mean "anonymous".
 */
export function normalizeExternalUserId(userId: string | undefined): string {
  const t = (userId ?? "").trim();
  if (!t || t === "null" || t === "None" || t === "undefined") {
    return "";
  }
  return t;
}

export function isIdentifiedPortalContext(
  context: SessionPortalContext
): boolean {
  return normalizeExternalUserId(context.user_id).length > 0;
}

export function getStoredResponseId(projectId: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(sessionStorageKey(projectId));
  } catch {
    return null;
  }
}

export function setStoredResponseId(
  projectId: string,
  responseId: string
): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  const id = responseId.trim();
  if (!id) return;
  try {
    window.localStorage.setItem(sessionStorageKey(projectId), id);
  } catch {
    // quota / disabled storage
  }
}

export function resolveResponseIdForPortalUrl(
  projectId: string,
  context: SessionPortalContext,
  options?: { skipAnonymousSessionLookup?: boolean }
): string | undefined {
  const explicit = context.response_id?.trim();
  if (isIdentifiedPortalContext(context)) {
    return explicit || undefined;
  }
  if (explicit) return explicit;
  if (options?.skipAnonymousSessionLookup) return undefined;
  const stored = getStoredResponseId(projectId);
  return stored?.trim() || undefined;
}

function tryParseMessageData(data: unknown): unknown {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as unknown;
    } catch {
      return null;
    }
  }
  return data;
}

function portalOrigin(baseUrl?: string): string {
  const raw = (baseUrl?.trim() && baseUrl) || getPortalBaseUrl();
  return new URL(raw).origin;
}

export interface WidgetSessionPersistenceOptions {
  projectId: string;
  baseUrl?: string;
  isAnonymous: () => boolean;
  onAnonymousSessionStored?: () => void;
}

/**
 * Persists `response_id` from legacy widget postMessages for anonymous users only.
 */
export function installWidgetSessionPersistence(
  options: WidgetSessionPersistenceOptions
): () => void {
  if (typeof window === "undefined") return () => {};

  const expectedOrigin = portalOrigin(options.baseUrl);
  const projectId = options.projectId.trim();

  const listener = (event: MessageEvent): void => {
    if (event.origin !== expectedOrigin) return;
    const parsed = tryParseMessageData(event.data);
    if (!parsed || typeof parsed !== "object") return;
    const data = parsed as Record<string, unknown>;
    if (data.source !== "usersound-widget") return;
    if (String(data.project_uuid ?? "") !== projectId) return;
    const type = data.type;
    if (type !== "interview_session" && type !== "interview_complete") return;
    const responseId = data.response_id;
    if (typeof responseId !== "string" || !responseId.trim()) return;
    if (!options.isAnonymous()) return;
    setStoredResponseId(projectId, responseId);
    options.onAnonymousSessionStored?.();
  };

  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}
